import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { SvgIconProp } from '../models/dynamic.model';
import { SmRouteKey } from '../rpe/sm-rpe.constants';
import { RpeFieldRegistry, RpeFormScope } from '../rpe/rpe.model';

export interface RpeFieldPermissionBridgeHandlers {
  applyToForm(
    moduleNames: readonly string[],
    form: FormGroup,
    registry: RpeFieldRegistry,
    pageMode: string,
    formScope: RpeFormScope
  ): void;
  applyToGridForm(
    moduleNames: readonly string[],
    gridForm: FormGroup,
    pageMode: string,
    formScope: RpeFormScope,
    registry: RpeFieldRegistry
  ): void;
  isIdentityPermitted(
    moduleNames: readonly string[],
    identityName: string,
    pageMode: string,
    formScope: RpeFormScope,
    registry: RpeFieldRegistry
  ): boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RpeRuntimeBridgeService {
  private menuVisibleFn: ((routerLink: string) => boolean) | null = null;
  private clearPermissionsFn: (() => void) | null = null;
  private fieldPermissionHandlers: RpeFieldPermissionBridgeHandlers | null =
    null;
  private smApplyInfoIconsFn:
    | ((
        route: SmRouteKey,
        icons: SvgIconProp[],
        isBulkEditView: boolean
      ) => SvgIconProp[])
    | null = null;
  private smSubscribeLocationRefreshFn: ((callback: () => void) => void) | null =
    null;
  private canMutateInfoTabsInViewModeFn: (() => boolean) | null = null;

  private readonly contextRefreshSubject = new Subject<void>();

  /** Emits when permissions resolve or active location changes. */
  public readonly contextRefresh$ = this.contextRefreshSubject.asObservable();

  public registerMenuVisibility(fn: (routerLink: string) => boolean): void {
    this.menuVisibleFn = fn;
    this.contextRefreshSubject.next();
  }

  public isMenuItemVisible(routerLink: string): boolean {
    return this.menuVisibleFn?.(routerLink) ?? true;
  }

  public notifyContextRefresh(): void {
    this.contextRefreshSubject.next();
  }

  public registerClearPermissions(fn: () => void): void {
    this.clearPermissionsFn = fn;
  }

  public clearEffectivePermissions(): void {
    this.clearPermissionsFn?.();
  }

  public registerFieldPermissionHandlers(
    handlers: RpeFieldPermissionBridgeHandlers
  ): void {
    this.fieldPermissionHandlers = handlers;
  }

  public applySharedTabFieldPermissions(
    moduleNames: readonly string[],
    form: FormGroup,
    registry: RpeFieldRegistry,
    pageMode: string,
    formScope: RpeFormScope
  ): void {
    this.fieldPermissionHandlers?.applyToForm(
      moduleNames,
      form,
      registry,
      pageMode,
      formScope
    );
  }

  public applySharedTabGridFieldPermissions(
    moduleNames: readonly string[],
    gridForm: FormGroup,
    pageMode: string,
    formScope: RpeFormScope,
    registry: RpeFieldRegistry
  ): void {
    this.fieldPermissionHandlers?.applyToGridForm(
      moduleNames,
      gridForm,
      pageMode,
      formScope,
      registry
    );
  }

  public isSharedTabIdentityPermitted(
    moduleNames: readonly string[],
    identityName: string,
    pageMode: string,
    formScope: RpeFormScope,
    registry: RpeFieldRegistry
  ): boolean {
    return (
      this.fieldPermissionHandlers?.isIdentityPermitted(
        moduleNames,
        identityName,
        pageMode,
        formScope,
        registry
      ) ?? true
    );
  }

  public registerSmCrudPopupHandlers(handlers: {
    applyInfoIcons: (
      route: SmRouteKey,
      icons: SvgIconProp[],
      isBulkEditView: boolean
    ) => SvgIconProp[];
    subscribeLocationRefresh: (callback: () => void) => void;
  }): void {
    this.smApplyInfoIconsFn = handlers.applyInfoIcons;
    this.smSubscribeLocationRefreshFn = handlers.subscribeLocationRefresh;
  }

  public applySmInfoIcons(
    route: SmRouteKey,
    icons: SvgIconProp[],
    isBulkEditView: boolean
  ): SvgIconProp[] {
    return this.smApplyInfoIconsFn?.(route, icons, isBulkEditView) ?? icons;
  }

  public subscribeSmLocationRefresh(callback: () => void): void {
    this.smSubscribeLocationRefreshFn?.(callback);
  }

  public registerCanMutateInformationTabsInViewMode(fn: () => boolean): void {
    this.canMutateInfoTabsInViewModeFn = fn;
  }

  /** FR-041 — View-mode roles/notes/attachments/statuscodes mutate. Default allow until registered. */
  public canMutateInformationTabsInViewMode(): boolean {
    return this.canMutateInfoTabsInViewModeFn?.() ?? true;
  }
}
